// ExcelImport — Phase 4 Plan 04-02 Task 2 (DIS-08..13).
// Migrated to telegram-ui — Phase 12 Plan 12-09 Task 1 (UI-SPEC §7 Wave 2).
//
// 4-step wizard state machine (logic PRESERVED — UI-SPEC §10 out-of-scope):
//   Step 1: Upload      — file picker + 3 sample download links
//   Step 2: Mapping     — MappingTable, [Back] / [Confirm mapping]
//   Step 3: Preview     — PreviewTable, inline edit, filter tabs,
//                         [Back] / [Continue to commit]
//   Step 4: Commit      — dual-button [Commit N valid, skip M invalid] /
//                         [Wait, I'll fix all first] OR success state
//
// telegram-ui migration (UI-SPEC §6/§7 — Wizard row):
//   - progress indicator → native <Steps count={4} progress={step-1}>
//   - errors → <Banner type="warning">
//   - secondary buttons → <Button mode="filled|bezeled|plain">
//   - screen shell → <List>; hardcode-hex / inline font-family removed (theme vars only)
//   - data-testid preserved (telegram-ui forwards ...restProps — Pitfall 6).
//
// Source:
//   - 04-CONTEXT.md D-66/D-67/D-69
//   - 12-UI-SPEC §7 Wave 2 (ExcelImport), §6 (migration map)
//   - apps/miniapp/src/components/Tabbar.tsx — telegram-ui import + theme-var pattern

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Banner, Button, List, Steps, Text, Title } from "@telegram-apps/telegram-ui";

import { ApiError } from "../api/client";
import {
  type ColumnMappingItem,
  type CommitResult,
  type PreviewRow,
  excelImportApi,
} from "../api/excelImport";
import { useAuth } from "../auth/useAuth";
import { MappingTable } from "../components/MappingTable";
import { PreviewTable } from "../components/PreviewTable";

type Step = 1 | 2 | 3 | 4;

const TOTAL_STEPS = 4;

export function ExcelImport(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();

  const [step, setStep] = useState<Step>(1);
  const [importId, setImportId] = useState<string | null>(null);
  const [autoMapping, setAutoMapping] = useState<ColumnMappingItem[]>([]);
  const [confirmedMapping, setConfirmedMapping] = useState<Record<string, string>>({});
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [validCount, setValidCount] = useState<number>(0);
  const [invalidCount, setInvalidCount] = useState<number>(0);
  const [filter, setFilter] = useState<"all" | "valid" | "errors">("all");
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
  const [isBusy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);

  function reset(): void {
    setStep(1);
    setImportId(null);
    setAutoMapping([]);
    setConfirmedMapping({});
    setPreviewRows([]);
    setValidCount(0);
    setInvalidCount(0);
    setCommitResult(null);
    setError(null);
  }

  async function handleFile(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await excelImportApi.upload(apiClient, file);
      setImportId(res.import_id);
      setAutoMapping(res.mapping);
      // Seed the confirmed mapping with the auto-detected values so the user
      // can review + override the low-confidence rows.
      const initial: Record<string, string> = {};
      for (const m of res.mapping) {
        initial[m.user_header] = m.mapped_to ?? "";
      }
      setConfirmedMapping(initial);
      setStep(2);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 413) {
          setError(
            t(
              "import.error.size",
              "File too large (max 10 MB). Save a smaller file and re-upload.",
            ),
          );
        } else if (err.status === 400) {
          setError(t("import.error.parse_failed"));
        } else {
          setError(err.detail);
        }
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmMapping(): Promise<void> {
    if (!importId) return;
    setBusy(true);
    setError(null);
    try {
      const filtered: Record<string, string> = {};
      for (const [h, target] of Object.entries(confirmedMapping)) {
        if (target && target !== "skip") {
          filtered[h] = target;
        }
      }
      const res = await excelImportApi.confirmMapping(apiClient, importId, filtered);
      setPreviewRows(res.rows);
      setValidCount(res.valid_count);
      setInvalidCount(res.invalid_count);
      setStep(3);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCellEdit(rowIndex: number, field: string, value: string): Promise<void> {
    if (!importId) return;
    try {
      const updated = await excelImportApi.revalidateRow(apiClient, importId, rowIndex, {
        [field]: value,
      });
      setPreviewRows((prev) => {
        const next = prev.map((r) => (r.row_index === rowIndex ? updated : r));
        const v = next.filter((r) => r.is_valid).length;
        const i = next.length - v;
        setValidCount(v);
        setInvalidCount(i);
        return next;
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  async function handleCommit(skipInvalid: boolean): Promise<void> {
    if (!importId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await excelImportApi.commit(apiClient, importId, skipInvalid);
      setCommitResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setBusy(false);
    }
  }

  // ----------------------------------------------------------------- //
  // Render
  // ----------------------------------------------------------------- //

  return (
    <List data-testid="excel-import-screen" style={{ padding: 16 }}>
      <Title level="2" weight="2" style={{ marginTop: 0, marginBottom: 12 }}>
        {t("import.title")}
      </Title>

      {/* Native progress indicator — Steps is 0-indexed, progress = step-1.
          telegram-ui <Steps> drops restProps, so the testid + a11y label live on
          a wrapping div (Pitfall 6 — testid must survive the migration). */}
      <div
        data-testid="excel-import-steps"
        aria-label={t("import.steps_aria", "Import progress")}
        style={{ marginBottom: 12 }}
      >
        <Steps count={TOTAL_STEPS} progress={step - 1} />
      </div>

      {error && (
        <Banner
          type="section"
          data-testid="excel-import-error"
          header={t("import.error.heading", "Import error")}
          description={error}
          style={{ marginBottom: 12 }}
        />
      )}

      {step === 1 && <Step1 isBusy={isBusy} onFile={handleFile} />}

      {step === 2 && (
        <Step2
          mapping={autoMapping}
          value={confirmedMapping}
          onChange={(h, v) => setConfirmedMapping((prev) => ({ ...prev, [h]: v }))}
          onBack={() => setShowDiscardConfirm(true)}
          onConfirm={handleConfirmMapping}
          isBusy={isBusy}
        />
      )}

      {step === 3 && (
        <Step3
          rows={previewRows}
          validCount={validCount}
          invalidCount={invalidCount}
          filter={filter}
          onFilter={setFilter}
          onCellEdit={handleCellEdit}
          onBack={() => setStep(2)}
          onContinue={() => setStep(4)}
        />
      )}

      {step === 4 && !commitResult && (
        <Step4
          validCount={validCount}
          invalidCount={invalidCount}
          isBusy={isBusy}
          onCommit={handleCommit}
          onBackToFix={() => setStep(3)}
        />
      )}

      {step === 4 && commitResult && <Step4Success result={commitResult} onNewImport={reset} />}

      {showDiscardConfirm && (
        <DiscardConfirm
          onCancel={() => setShowDiscardConfirm(false)}
          onConfirm={() => {
            setShowDiscardConfirm(false);
            reset();
          }}
        />
      )}
    </List>
  );
}

// ----------------------------------------------------------------- //
// Step 1 — Upload
// ----------------------------------------------------------------- //

function Step1({
  isBusy,
  onFile,
}: {
  isBusy: boolean;
  onFile: (f: File) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div data-testid="excel-import-step1">
      <Text style={{ color: "var(--tgui--hint_color)" }}>{t("import.step1.subtitle")}</Text>
      <label
        data-testid="excel-import-dropzone"
        style={{
          display: "block",
          border: "2px dashed var(--tgui--outline, var(--tgui--hint_color))",
          padding: 24,
          borderRadius: 12,
          textAlign: "center",
          cursor: isBusy ? "wait" : "pointer",
          background: "var(--tgui--secondary_bg_color)",
          marginTop: 8,
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }} aria-hidden>
          📊
        </div>
        <Text weight="2" style={{ display: "block", marginBottom: 4 }}>
          {t("import.step1.dropzone.heading")}
        </Text>
        <Text style={{ display: "block", color: "var(--tgui--hint_color)", marginBottom: 12 }}>
          {t("import.step1.dropzone.or")}
        </Text>
        <input
          type="file"
          accept=".xlsx,.xls"
          data-testid="excel-import-file-input"
          disabled={isBusy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
          style={{ marginBottom: 12 }}
        />
        <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
          {t("import.step1.encodings")}
        </Text>
        <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
          {t("import.step1.size_limit")}
        </Text>
      </label>
      <div style={{ marginTop: 16 }}>
        <Text style={{ display: "block", color: "var(--tgui--hint_color)", marginBottom: 8 }}>
          {t("import.step1.sample")}
        </Text>
        <a
          data-testid="excel-import-sample-en"
          href="/static/samples/import_sample_en.xlsx"
          style={{ display: "block", color: "var(--tgui--link_color)", marginBottom: 4 }}
        >
          Download sample .xlsx (EN)
        </a>
        <a
          data-testid="excel-import-sample-tr"
          href="/static/samples/import_sample_tr.xlsx"
          style={{ display: "block", color: "var(--tgui--link_color)", marginBottom: 4 }}
        >
          Download sample .xlsx (TR)
        </a>
        <a
          data-testid="excel-import-sample-bg"
          href="/static/samples/import_sample_bg.xlsx"
          style={{ display: "block", color: "var(--tgui--link_color)" }}
        >
          Download sample .xlsx (BG)
        </a>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- //
// Step 2 — Confirm mapping
// ----------------------------------------------------------------- //

function Step2({
  mapping,
  value,
  onChange,
  onBack,
  onConfirm,
  isBusy,
}: {
  mapping: ColumnMappingItem[];
  value: Record<string, string>;
  onChange: (h: string, v: string) => void;
  onBack: () => void;
  onConfirm: () => void;
  isBusy: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const detected = mapping.filter((m) => !m.needs_manual && m.mapped_to).length;
  return (
    <div data-testid="excel-import-step2">
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        {t("import.step2.subtitle")}
      </Text>
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        {t("import.step2.detected", { detected, total: mapping.length })}
      </Text>
      <MappingTable mapping={mapping} value={value} onChange={onChange} />
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <Button
          mode="bezeled"
          size="m"
          data-testid="excel-import-step2-back"
          onClick={onBack}
          disabled={isBusy}
        >
          ← {t("common.back", "Back")}
        </Button>
        <Button
          mode="filled"
          size="m"
          stretched
          data-testid="excel-import-step2-confirm"
          onClick={onConfirm}
          disabled={isBusy}
        >
          {t("import.step2.confirm_mapping")}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- //
// Step 3 — Review & fix
// ----------------------------------------------------------------- //

function Step3({
  rows,
  validCount,
  invalidCount,
  filter,
  onFilter,
  onCellEdit,
  onBack,
  onContinue,
}: {
  rows: PreviewRow[];
  validCount: number;
  invalidCount: number;
  filter: "all" | "valid" | "errors";
  onFilter: (f: "all" | "valid" | "errors") => void;
  onCellEdit: (rowIndex: number, field: string, value: string) => void;
  onBack: () => void;
  onContinue: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const total = rows.length;
  return (
    <div data-testid="excel-import-step3">
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        {t("import.step3.subtitle")}
      </Text>
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        {t("import.step3.summary", { valid: validCount, invalid: invalidCount })}
      </Text>
      <div data-testid="excel-import-filters" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Button
          mode={filter === "all" ? "filled" : "bezeled"}
          size="s"
          data-testid="filter-all"
          onClick={() => onFilter("all")}
        >
          {t("import.step3.filter.all", { count: total })}
        </Button>
        <Button
          mode={filter === "valid" ? "filled" : "bezeled"}
          size="s"
          data-testid="filter-valid"
          onClick={() => onFilter("valid")}
        >
          {t("import.step3.filter.valid", { count: validCount })}
        </Button>
        <Button
          mode={filter === "errors" ? "filled" : "bezeled"}
          size="s"
          data-testid="filter-errors"
          onClick={() => onFilter("errors")}
        >
          {t("import.step3.filter.errors", { count: invalidCount })}
        </Button>
      </div>
      <PreviewTable rows={rows} onCellEdit={onCellEdit} filter={filter} />
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <Button mode="bezeled" size="m" data-testid="excel-import-step3-back" onClick={onBack}>
          ← {t("common.back", "Back")}
        </Button>
        <Button
          mode="filled"
          size="m"
          stretched
          data-testid="excel-import-step3-continue"
          onClick={onContinue}
        >
          {t("import.step3.continue")}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- //
// Step 4 — Commit (dual-button)
// ----------------------------------------------------------------- //

function Step4({
  validCount,
  invalidCount,
  isBusy,
  onCommit,
  onBackToFix,
}: {
  validCount: number;
  invalidCount: number;
  isBusy: boolean;
  onCommit: (skipInvalid: boolean) => void;
  onBackToFix: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const hasInvalid = invalidCount > 0;

  if (!hasInvalid) {
    return (
      <div data-testid="excel-import-step4">
        <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
          {t("import.step4.subtitle")}
        </Text>
        <Title level="3" weight="2">
          {validCount} valid, 0 invalid
        </Title>
        <Button
          mode="filled"
          size="l"
          stretched
          data-testid="excel-import-commit-all"
          onClick={() => onCommit(false)}
          disabled={isBusy}
          loading={isBusy}
        >
          Create {validCount} trips
        </Button>
      </div>
    );
  }

  // Dual-button per D-67
  return (
    <div data-testid="excel-import-step4">
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        {t("import.step4.subtitle")}
      </Text>
      <Title level="3" weight="2">
        {validCount} valid, {invalidCount} invalid
      </Title>
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        You can commit {validCount} valid rows now and skip the {invalidCount} invalid ones, or go
        back and fix all errors first.
      </Text>
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
        <Button
          mode="filled"
          size="l"
          stretched
          data-testid="excel-import-commit-partial"
          onClick={() => onCommit(true)}
          disabled={isBusy}
          loading={isBusy}
        >
          {t("import.step4.commit_partial", {
            valid: validCount,
            invalid: invalidCount,
          })}
        </Button>
        <Button
          mode="bezeled"
          size="l"
          stretched
          data-testid="excel-import-fix-first"
          onClick={onBackToFix}
          disabled={isBusy}
        >
          {t("import.step4.fix_first")}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- //
// Step 4 — Success state
// ----------------------------------------------------------------- //

function Step4Success({
  result,
  onNewImport,
}: {
  result: CommitResult;
  onNewImport: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div data-testid="excel-import-step4-success">
      <Title level="3" weight="2" style={{ color: "var(--tgui--green, var(--tgui--text_color))" }}>
        {t("import.step4.success.heading", { count: result.created })}
      </Title>
      <Text style={{ display: "block", color: "var(--tgui--hint_color)" }}>
        {t("import.step4.success.body", {
          skipped: result.skipped + result.errors.length,
        })}
      </Text>
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
        <Button
          mode="filled"
          size="l"
          stretched
          data-testid="excel-import-view-trips"
          onClick={() => {
            // Navigation to /dispatcher/trips will be wired in a downstream
            // plan (04-05 Dashboard / 04-07 Trips). For now the button stays
            // visible but inert — clicked-state still emits the testid.
          }}
        >
          {t("import.step4.view_trips")}
        </Button>
        <Button
          mode="bezeled"
          size="l"
          stretched
          data-testid="excel-import-new-import"
          onClick={onNewImport}
        >
          {t("import.step4.new_import")}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- //
// Discard confirm modal (destructive — Step 2 → back to upload)
// ----------------------------------------------------------------- //

function DiscardConfirm({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid="excel-import-discard-confirm"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: "var(--tgui--bg_color)",
          padding: 24,
          borderRadius: 12,
          maxWidth: 320,
        }}
      >
        <Title level="3" weight="2" style={{ marginTop: 0 }}>
          {t("import.confirm.discard.title")}
        </Title>
        <Text style={{ display: "block", marginBottom: 12 }}>
          {t("import.confirm.discard.body")}
        </Text>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button mode="plain" size="m" onClick={onCancel}>
            {t("import.confirm.discard.cancel")}
          </Button>
          <Button
            mode="filled"
            size="m"
            data-testid="excel-import-discard-confirm-ok"
            onClick={onConfirm}
            style={{ background: "var(--tgui--destructive_text_color, var(--tgui--link_color))" }}
          >
            {t("import.confirm.discard.ok")}
          </Button>
        </div>
      </div>
    </div>
  );
}
